<template>
  <div class="card p-4 shadow-sm">
    <div class="container-fluid my-5">
      <h3 class="text-center mb-4">Gestión de Reservas</h3>

      <!-- Filtros rápidos -->
      <div class="row mb-3 g-2">
        <div class="col-auto">
          <button
            :class="[
              'btn btn-sm',
              filtroRapido === 'todas' ? 'btn-dark' : 'btn-outline-dark',
            ]"
            @click="aplicarFiltroRapido('todas')"
          >
            📋 Todas
          </button>
        </div>
        <div class="col-auto">
          <button
            :class="[
              'btn btn-sm',
              filtroRapido === 'checkins_hoy'
                ? 'btn-success'
                : 'btn-outline-success',
            ]"
            @click="aplicarFiltroRapido('checkins_hoy')"
          >
            ✔ Check-ins hoy
          </button>
        </div>
        <div class="col-auto">
          <button
            :class="[
              'btn btn-sm',
              filtroRapido === 'checkouts_hoy'
                ? 'btn-warning'
                : 'btn-outline-warning',
            ]"
            @click="aplicarFiltroRapido('checkouts_hoy')"
          >
            ⏬ Check-outs hoy
          </button>
        </div>
        <div class="col-auto">
          <button
            :class="[
              'btn btn-sm',
              filtroRapido === 'pagadas'
                ? 'btn-secondary'
                : 'btn-outline-secondary',
            ]"
            @click="aplicarFiltroRapido('pagadas')"
          >
            💰 Pagadas
          </button>
        </div>
        <div class="col-auto">
          <button
            :class="[
              'btn btn-sm',
              filtroRapido === 'confirmadas' ? 'btn-info' : 'btn-outline-info',
            ]"
            @click="aplicarFiltroRapido('confirmadas')"
          >
            &#10003; Confirmadas
          </button>
        </div>
        <div class="col-auto">
          <button
            :class="[
              'btn btn-sm',
              filtroRapido === 'no_confirmadas'
                ? 'btn-warning'
                : 'btn-outline-warning',
            ]"
            @click="aplicarFiltroRapido('no_confirmadas')"
          >
            &#9203; Sin confirmar
          </button>
        </div>
      </div>

      <!-- Filtros -->
      <div class="row g-3 mb-4 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Buscar</label>
          <input
            type="text"
            class="form-control"
            placeholder="Nombre, código (R00#), # habitación, NIT o DPI"
            v-model="busqueda"
            @input="filtrarPorTexto"
          />
        </div>

        <div class="col-md-3">
          <label class="form-label">Estado de reserva</label>
          <select
            class="form-select"
            v-model="estado"
            @change="filtrarPorEstado"
          >
            <option value="">Activas (Pendiente + En curso)</option>
            <option value="0">Pendiente</option>
            <option value="1">En curso</option>
            <option value="2">Finalizada</option>
            <option value="3">Cancelada</option>
          </select>
        </div>

        <div class="col-md-5">
          <label class="form-label">Rango de fechas</label>
          <div class="row g-2">
            <div class="col-md-6">
              <input
                type="date"
                class="form-control"
                v-model="desde"
                @change="filtrarPorFechas"
              />
            </div>
            <div class="col-md-6">
              <input
                type="date"
                class="form-control"
                v-model="hasta"
                @change="filtrarPorFechas"
              />
            </div>
          </div>
        </div>

        <div class="col-md-2 d-flex align-items-end ms-auto">
          <button class="btn btn-secondary w-100" @click="limpiarFiltros">
            Limpiar Filtros
          </button>
        </div>
      </div>

      <!-- Tabla -->
      <div class="table-responsive" v-if="reservas.length">
        <table class="table table-bordered table-hover align-middle">
          <thead class="bg-light text-center">
            <tr>
              <th>Código</th>
              <th>Fecha Llegada</th>
              <th>Fecha Salida</th>
              <th>Cliente</th>
              <th>Total (Q)</th>
              <th>Saldo Pendiente</th>
              <th>Estado de Pago</th>
              <th>Estado Reserva</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="reserva in reservas" :key="reserva.IdReserva">
              <td>
                <strong>R00{{ reserva.IdReserva }}</strong>
                <div class="text-muted small mt-1">
                  Habitaciones:
                  <strong>{{ obtenerNumerosHabitaciones(reserva) }}</strong>
                </div>
              </td>
              <td class="nowrap">{{ formatearFecha(reserva.FechaLlegada) }}</td>
              <td class="nowrap">{{ formatearFecha(reserva.FechaSalida) }}</td>
              <td>
                {{ reserva.huesped?.Nombre }} {{ reserva.huesped?.Apellido }}
              </td>
              <td>
                Q.{{
                  (
                    Number(reserva.TotalReserva || 0) +
                    Number(reserva.TotalOtros || 0)
                  ).toFixed(2)
                }}
              </td>
              <td>Q.{{ Number(reserva.SaldoPendiente || 0).toFixed(2) }}</td>
              <td>
                <span
                  v-if="Number(reserva.SaldoPendiente) <= 0"
                  class="badge bg-success"
                >
                  Pagado
                </span>
                <span v-else class="badge bg-danger"> Pendiente </span>
              </td>
              <td>
                <span
                  :class="{
                    'badge bg-warning text-dark': reserva.EstadoReserva === 0,
                    'badge bg-primary': reserva.EstadoReserva === 1,
                    'badge bg-success': reserva.EstadoReserva === 2,
                    'badge bg-danger': reserva.EstadoReserva === 3,
                  }"
                >
                  {{
                    ["Pendiente", "En curso", "Finalizada", "Cancelada"][
                      reserva.EstadoReserva
                    ]
                  }}
                </span>
                <div
                  v-if="reserva.ConfirmadaPorHuesped"
                  class="badge bg-info mt-1 d-block"
                >
                  ✓ Confirmada
                </div>
              </td>
              <td>
                <div class="d-flex flex-wrap justify-content-center gap-2">
                  <button
                    class="btn btn-sm btn-outline-info"
                    @click="verDetalles(reserva)"
                  >
                    👁 Ver
                  </button>
                  <button
                    v-if="reserva.EstadoReserva !== 3"
                    class="btn btn-sm btn-outline-warning"
                    @click="pagarReserva(reserva)"
                  >
                    💳 Pagos
                  </button>
                  <button
                    v-if="
                      tieneHabitacionesSinCheckIn(reserva) &&
                      reserva.EstadoReserva !== 2
                    "
                    class="btn btn-sm btn-outline-success"
                    @click="checkIn(reserva)"
                  >
                    ✔ Check-In
                  </button>
                  <button
                    v-if="
                      tieneHabitacionesSinCheckOut(reserva) &&
                      reserva.EstadoReserva !== 2 &&
                      Number(reserva.SaldoPendiente) <= 0
                    "
                    class="btn btn-sm btn-outline-danger"
                    @click="checkOut(reserva)"
                  >
                    ⏬ Check-Out
                  </button>
                  <button
                    v-if="
                      reserva.EstadoReserva === 0 || reserva.EstadoReserva === 1
                    "
                    class="btn btn-sm btn-outline-primary"
                    @click="editarFechas(reserva)"
                  >
                    ✏️ Editar Reserva
                  </button>
                  <button
                    v-if="
                      esAdmin &&
                      (reserva.EstadoReserva === 0 ||
                        reserva.EstadoReserva === 1)
                    "
                    class="btn btn-sm btn-outline-danger"
                    @click="abrirModalCancelar(reserva)"
                  >
                    ✖ Cancelar
                  </button>
                  <button
                    v-if="esAdminORecepcionista && reserva.EstadoReserva === 2"
                    class="btn btn-sm btn-outline-warning"
                    @click="revertirCheckoutReserva(reserva)"
                    title="Revertir checkout"
                  >
                    ↩ Revertir Checkout
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center text-muted fs-5">
        No se encontraron reservas.
      </div>

      <!-- Paginación numerada -->
      <div v-if="pagination.total > 0" class="mt-4">
        <nav class="d-flex justify-content-center">
          <ul class="pagination shadow-sm">
            <li :class="['page-item', { disabled: !pagination.prev_page_url }]">
              <button
                class="page-link"
                @click="cambiarPagina(pagination.current_page - 1)"
              >
                ← Anterior
              </button>
            </li>

            <li
              v-for="page in paginas"
              :key="page"
              :class="[
                'page-item',
                { active: page === pagination.current_page },
              ]"
            >
              <button class="page-link" @click="cambiarPagina(page)">
                {{ page }}
              </button>
            </li>

            <li :class="['page-item', { disabled: !pagination.next_page_url }]">
              <button
                class="page-link"
                @click="cambiarPagina(pagination.current_page + 1)"
              >
                Siguiente →
              </button>
            </li>
          </ul>
        </nav>

        <!-- Texto debajo -->
        <div class="text-center text-muted mt-2">
          Mostrando {{ pagination.from }}–{{ pagination.to }} de
          {{ pagination.total }} resultados
        </div>
      </div>
    </div>

    <!-- Modal Revertir Checkout -->
    <div
      v-if="mostrarModalRevertirCheckout"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-warning">
            <h5 class="modal-title">&#8617; Revertir Checkout</h5>
            <button
              type="button"
              class="btn-close"
              @click="cerrarModalRevertirCheckout"
            ></button>
          </div>
          <div class="modal-body text-center py-4">
            <i
              class="bi bi-arrow-counterclockwise text-warning"
              style="font-size: 3rem"
            ></i>
            <p class="mt-3 mb-0 fs-5">¿Revertir el checkout de esta reserva?</p>
            <p class="text-muted mt-2" v-if="reservaRevertir">
              <strong>Código:</strong> R00{{ reservaRevertir.IdReserva }}<br />
              <strong>Huésped:</strong>
              {{ reservaRevertir.huesped?.Nombre }}
              {{ reservaRevertir.huesped?.Apellido }}<br />
              <strong>Habitaciones:</strong>
              {{ obtenerNumerosHabitaciones(reservaRevertir) }}
            </p>
            <p class="text-muted small mt-2">
              La reserva volverá a estado Activo y las habitaciones a Ocupadas.
            </p>
          </div>
          <div class="modal-footer justify-content-center">
            <button
              type="button"
              class="btn btn-secondary px-4"
              @click="cerrarModalRevertirCheckout"
              :disabled="reviertiendo"
            >
              No, Volver
            </button>
            <button
              type="button"
              class="btn btn-warning px-4"
              @click="confirmarRevertirCheckout"
              :disabled="reviertiendo"
            >
              <span
                v-if="reviertiendo"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              {{ reviertiendo ? "Revirtiendo..." : "Sí, Revertir Checkout" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Cancelar Reserva -->
    <div
      v-if="mostrarModalCancelar"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title">✖ Cancelar Reserva</h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              @click="cerrarModalCancelar"
            ></button>
          </div>
          <div class="modal-body text-center py-4">
            <i
              class="bi bi-exclamation-triangle text-danger"
              style="font-size: 3rem"
            ></i>
            <p class="mt-3 mb-0 fs-5">
              ¿Está seguro que desea cancelar esta reserva?
            </p>
            <p class="text-muted mt-2" v-if="reservaCancelar">
              <strong>Código:</strong> R00{{ reservaCancelar.IdReserva }}<br />
              <strong>Huésped:</strong>
              {{ reservaCancelar.huesped?.Nombre }}
              {{ reservaCancelar.huesped?.Apellido }}<br />
              <strong>Habitaciones:</strong>
              {{ obtenerNumerosHabitaciones(reservaCancelar) }}
            </p>
            <p class="text-muted small mt-2">
              No se podrá cancelar si la reserva tiene pagos registrados o
              consumos de restaurante.
            </p>
          </div>
          <div class="modal-footer justify-content-center">
            <button
              type="button"
              class="btn btn-secondary px-4"
              @click="cerrarModalCancelar"
              :disabled="cancelando"
            >
              No, Volver
            </button>
            <button
              type="button"
              class="btn btn-danger px-4"
              @click="confirmarCancelacion"
              :disabled="cancelando"
            >
              <span
                v-if="cancelando"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              {{ cancelando ? "Cancelando..." : "Sí, Cancelar Reserva" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast notifications -->
    <div
      class="toast-container position-fixed top-0 end-0 p-3"
      style="z-index: 9999"
    >
      <div
        v-if="mostrarToast"
        class="toast show"
        :class="{
          'bg-success': tipoToast === 'success',
          'bg-danger': tipoToast === 'danger',
          'bg-warning': tipoToast === 'warning',
        }"
        role="alert"
      >
        <div class="toast-header">
          <i
            class="bx me-2"
            :class="{
              'bx-check-circle': tipoToast === 'success',
              'bx-error-circle': tipoToast === 'danger',
              'bx-info-circle': tipoToast === 'warning',
            }"
          ></i>
          <strong class="me-auto">{{
            tipoToast === "success"
              ? "Éxito"
              : tipoToast === "danger"
              ? "Error"
              : "Advertencia"
          }}</strong>
          <button type="button" class="btn-close" @click="cerrarToast"></button>
        </div>
        <div class="toast-body text-white">
          {{ mensajeToast }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL } from "@/config/config.js";

export default {
  name: "ReservasGestion",
  data() {
    return {
      reservas: [],
      busqueda: "",
      estado: "",
      desde: "",
      hasta: "",
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      },
      filtrosActivos: {},
      debounceTimer: null,
      mostrarToast: false,
      tipoToast: "success",
      mensajeToast: "",
      filtroRapido: null,
      mostrarModalCancelar: false,
      reservaCancelar: null,
      cancelando: false,
      mostrarModalRevertirCheckout: false,
      reservaRevertir: null,
      reviertiendo: false,
    };
  },
  computed: {
    esAdminORecepcionista() {
      const rol = this.$store.getters.getUser?.IdRol;
      return rol === 1 || rol === 2;
    },
    esAdmin() {
      return this.$store.getters.getUser?.IdRol === 1;
    },
    paginas() {
      const total = this.pagination.last_page;
      const actual = this.pagination.current_page;
      const rango = 2;
      const inicio = Math.max(1, actual - rango);
      const fin = Math.min(total, actual + rango);
      const paginas = [];
      for (let i = inicio; i <= fin; i++) paginas.push(i);
      return paginas;
    },
  },
  mounted() {
    const guardados = localStorage.getItem("filtros_reservas");

    if (guardados) {
      const filtros = JSON.parse(guardados);

      this.busqueda = filtros.q || "";
      this.estado = filtros.estado ?? "";
      this.desde = filtros.desde || "";
      this.hasta = filtros.hasta || "";
      // Restaurar filtro rápido activo
      if (filtros.todas) this.filtroRapido = "todas";
      else if (filtros.checkins_hoy) this.filtroRapido = "checkins_hoy";
      else if (filtros.checkouts_hoy) this.filtroRapido = "checkouts_hoy";
      else if (filtros.pagadas) this.filtroRapido = "pagadas";
      else if (filtros.confirmadas) this.filtroRapido = "confirmadas";
      else if (filtros.no_confirmadas) this.filtroRapido = "no_confirmadas";

      this.buscarReservas(filtros);
    } else {
      // Por defecto no enviamos estado, el backend devuelve Pendiente (0) y En Curso (1)
      this.buscarReservas({});
    }
  },
  methods: {
    aplicarFiltroRapido(tipo) {
      this.filtroRapido = tipo;
      this.busqueda = "";
      this.estado = "";
      this.desde = "";
      this.hasta = "";
      const filtros = { [tipo]: 1 };
      localStorage.setItem("filtros_reservas", JSON.stringify(filtros));
      this.buscarReservas(filtros);
    },
    async buscarReservas(filtros = {}, page = 1) {
      try {
        const res = await axios.get(`${API_URL}/reservas/buscar-avanzado`, {
          params: { ...filtros, page },
        });
        this.reservas = res.data.data;
        this.pagination = res.data;
        this.filtrosActivos = filtros;
      } catch (error) {
        console.error("Error al buscar reservas:", error);
      }
    },
    cambiarPagina(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.buscarReservas(this.filtrosActivos, page);
      }
    },
    limpiarFiltros() {
      this.busqueda = "";
      this.estado = "";
      this.desde = "";
      this.hasta = "";
      this.filtroRapido = null;

      localStorage.removeItem("filtros_reservas");
      this.buscarReservas({});
    },
    filtrarPorTexto() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        if (this.busqueda.trim()) {
          // Buscar en TODAS las reservas (sin filtro de estado)
          this.filtroRapido = null;
          this.estado = "";
          this.desde = "";
          this.hasta = "";
          const filtros = { q: this.busqueda.trim(), todas: 1 };
          localStorage.setItem("filtros_reservas", JSON.stringify(filtros));
          this.buscarReservas(filtros);
        } else {
          this.filtroRapido = null;
          this.estado = "";
          this.desde = "";
          this.hasta = "";
          localStorage.removeItem("filtros_reservas");
          this.buscarReservas({});
        }
      }, 500);
    },
    filtrarPorEstado() {
      this.filtroRapido = null;
      this.busqueda = "";
      this.desde = "";
      this.hasta = "";

      const filtros = { estado: this.estado };
      localStorage.setItem("filtros_reservas", JSON.stringify(filtros));
      this.buscarReservas(filtros);
    },
    filtrarPorFechas() {
      this.busqueda = "";
      this.estado = "";

      if (this.desde && this.hasta) {
        const filtros = { desde: this.desde, hasta: this.hasta };
        localStorage.setItem("filtros_reservas", JSON.stringify(filtros));
        this.buscarReservas(filtros);
      } else {
        const filtros = { estado: 0 };
        localStorage.setItem("filtros_reservas", JSON.stringify(filtros));
        this.buscarReservas(filtros);
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return "";
      const [year, month, day] = fecha.split("T")[0].split("-");
      return `${day}/${month}/${year}`;
    },
    verDetalles(reserva) {
      this.$router.push({
        name: "VerDetallesReservas",
        params: { id: reserva.IdReserva },
      });
    },
    pagarReserva(reserva) {
      this.$router.push({
        name: "PagarReserva",
        params: { id: reserva.IdReserva },
      });
    },
    checkIn(reserva) {
      this.$router.push({
        name: "checkInHabitaciones",
        params: { id: reserva.IdReserva },
      });
    },
    checkOut(reserva) {
      this.$router.push({
        name: "checkOutHabitaciones",
        params: { id: reserva.IdReserva },
      });
    },
    obtenerNumerosHabitaciones(reserva) {
      if (reserva.habitaciones_info && reserva.habitaciones_info.length > 0) {
        return reserva.habitaciones_info.map((h) => h.Numero).join(", ");
      }
      return "Sin asignar";
    },
    confirmarDiaExtra(reserva) {
      this.$router.push({
        name: "ValidarDiaExtraF",
        params: { id: reserva.IdReserva },
      });
    },
    editarFechas(reserva) {
      this.$router.push({
        name: "ValidarDiaExtraF",
        params: { id: reserva.IdReserva },
      });
    },
    abrirModalCancelar(reserva) {
      this.reservaCancelar = reserva;
      this.mostrarModalCancelar = true;
    },
    cerrarModalCancelar() {
      this.mostrarModalCancelar = false;
      this.reservaCancelar = null;
    },
    async confirmarCancelacion() {
      if (!this.reservaCancelar) return;
      this.cancelando = true;
      try {
        const res = await axios.put(
          `${API_URL}/reservas/${this.reservaCancelar.IdReserva}/cancelar`,
        );
        this.cerrarModalCancelar();
        this.mostrarToastMensaje(
          res.data.message || "Reserva cancelada correctamente.",
          "success",
        );
        // Recargar la lista
        this.buscarReservas(this.filtrosActivos, this.pagination.current_page);
      } catch (error) {
        const msg =
          error.response?.data?.message || "Error al cancelar la reserva.";
        this.mostrarToastMensaje(msg, "danger");
      } finally {
        this.cancelando = false;
      }
    },
    tieneHabitacionesSinCheckIn(reserva) {
      // Si no hay datos de habitaciones, mostrar botón (mejor ser permisivo)
      const habitaciones =
        reserva.habitacionesReservadas || reserva.habitaciones_reservadas;
      if (!habitaciones || habitaciones.length === 0) {
        return true; // Mostrar botón si no hay datos
      }
      // Mostrar si al menos una habitación NO tiene CheckIn
      return habitaciones.some((hab) => !hab.CheckIn);
    },
    tieneAlgunaHabitacionConCheckIn(reserva) {
      const habitaciones =
        reserva.habitacionesReservadas || reserva.habitaciones_reservadas;
      if (!habitaciones || habitaciones.length === 0) {
        return false; // No tiene check-in si no hay datos
      }
      // Retorna true si al menos una habitación YA tiene CheckIn
      return habitaciones.some((hab) => hab.CheckIn);
    },
    tieneHabitacionesSinCheckOut(reserva) {
      const habitaciones =
        reserva.habitacionesReservadas || reserva.habitaciones_reservadas;
      if (!habitaciones || habitaciones.length === 0) {
        return false; // No mostrar si no hay datos
      }
      // Solo mostrar CheckOut si:
      // 1. Al menos una habitación YA tiene CheckIn (se hizo check-in)
      // 2. Al menos una habitación NO tiene CheckOut (falta hacer checkout)
      // 3. La reserva está completamente pagada (validación adicional en v-if del botón)
      const tieneAlgunaConCheckIn = habitaciones.some((hab) => hab.CheckIn);
      const tieneSinCheckOut = habitaciones.some((hab) => !hab.CheckOut);
      return tieneAlgunaConCheckIn && tieneSinCheckOut;
    },
    async revertirCheckoutReserva(reserva) {
      this.reservaRevertir = reserva;
      this.mostrarModalRevertirCheckout = true;
    },
    cerrarModalRevertirCheckout() {
      this.mostrarModalRevertirCheckout = false;
      this.reservaRevertir = null;
    },
    async confirmarRevertirCheckout() {
      if (!this.reservaRevertir) return;
      this.reviertiendo = true;
      try {
        const { data } = await axios.put(
          `${API_URL}/reservas/${this.reservaRevertir.IdReserva}/revertir-checkout`,
        );
        this.cerrarModalRevertirCheckout();
        this.mostrarToastMensaje(
          data.message || "Checkout revertido correctamente",
          "success",
        );
        this.buscarReservas(this.filtrosActivos, this.pagination.current_page);
      } catch (err) {
        const msg =
          err.response?.data?.message || "Error al revertir el checkout";
        this.mostrarToastMensaje(msg, "danger");
      } finally {
        this.reviertiendo = false;
      }
    },
    mostrarToastMensaje(mensaje, tipo = "success") {
      this.mensajeToast = mensaje;
      this.tipoToast = tipo;
      this.mostrarToast = true;
      setTimeout(() => {
        this.cerrarToast();
      }, 4000);
    },
    cerrarToast() {
      this.mostrarToast = false;
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
  text-align: center;
}
.btn {
  min-width: 90px;
}
.nowrap {
  white-space: nowrap;
}
.pagination {
  border-radius: 8px;
}
.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: white;
}
.pagination .page-link {
  cursor: pointer;
  border-radius: 6px;
  margin: 0 2px;
}
.small {
  font-size: 0.8rem;
}
</style>
